import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, map, Observable, tap } from 'rxjs';
import { Provider } from '../models/provider';
import { ProviderSearch } from '../models/providerSearch';
import { Professional } from '../models/professional';
import { ProfessionalSearch } from '../models/professionalSearch';
import { NotificationService } from './notification.service';
import { Store } from '@ngrx/store';
import { saveAs } from 'file-saver';
import { cleanSearchParams, handleError } from '../shared/helper/helpers-functions';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  private url = environment.pvtUrl;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private store: Store
  ) {}

  private log(message: string) {
    console.log(message);
  }

  addSearch(payload: {
    benefit: string;
    province: string;
    profession: string;
    firstName: string;
    lastName: string;
  }): Observable<any> {
    const url = `${this.url}/professionals/addSearch`;
    return this.http.post(url, payload, httpOptions).pipe(
      tap((_) => this.log(`addSearch`)),
      catchError(handleError<Provider[]>(`addSearch`, this.notificationService, this.store))
    );
  }

  getAllProviders(): Observable<Provider[]> {
    const url = `${this.url}/providers/all`;
    return this.http.get<Provider[]>(url).pipe(
      tap((_) => this.log(`getAllProviders`)),
      catchError(handleError<Provider[]>(`getAllProviders`, this.notificationService, this.store))
    );
  }

  getProviderSearch(): Observable<ProviderSearch> {
    const url = `${this.url}/providers/providerSearch`;
    return this.http.get<ProviderSearch>(url, httpOptions).pipe(
      tap((_) => this.log(`getProviderSearch`)),
      catchError(handleError<ProviderSearch>(`getProviderSearch`, this.notificationService, this.store))
    );
  }

  searchProviders(providerSearch: ProviderSearch): Observable<Provider[]> {
    const url = `${this.url}/providers/search`;
    return this.http
      .post<Provider[]>(
        url,
        cleanSearchParams<ProviderSearch>(providerSearch),
        httpOptions
      )
      .pipe(
        map((providers: Provider[]) =>
          this.addConcernsFlag<Provider>(providers)
        ),
        tap((_) => this.log(`searchProviders`)),
        catchError(handleError<Provider[]>(`searchProviders`, this.notificationService, this.store))
      );
  }

  searchPaginatedProviders(
    providersSearch: ProviderSearch
  ): Observable<ProviderSearch> {
    const url = `${this.url}/providers/search2`;
    return this.http
      .post<ProviderSearch>(
        url,
        cleanSearchParams<ProviderSearch>(providersSearch),
        httpOptions
      )
      .pipe(
        map((providersResults: ProviderSearch) => {
          providersResults.pageResult.content = this.addConcernsFlag<Provider>(
            providersResults.pageResult.content
          );
          return providersResults;
        }),
        tap((_) => this.log(`searchPaginatedProviders`)),
        catchError(handleError<ProviderSearch>(`searchPaginatedProviders`, this.notificationService, this.store))
      );
  }

  getAllProfessionals(): Observable<Professional[]> {
    const url = `${this.url}/professionals/all`;
    return this.http.get<Professional[]>(url).pipe(
      tap((_) => this.log(`getAllProfessionals`)),
      catchError(
        handleError<Professional[]>(
          `getAllProfessionals`,
          this.notificationService,
          this.store
        )
      )
    );
  }

  getProfessionalSearch(): Observable<ProfessionalSearch> {
    const url = `${this.url}/professionals/professionalSearch`;
    return this.http.get<ProfessionalSearch>(url, httpOptions).pipe(
      tap((_) => this.log(`getProfessionalSearch`)),
      catchError(handleError<ProfessionalSearch>(`getProfessionalSearch`, this.notificationService, this.store))
    );
  }

  searchProfessionals(
    professionalSearch: ProfessionalSearch
  ): Observable<Professional[]> {
    const url = `${this.url}/professionals/search`;
    return this.http
      .post<Professional[]>(
        url,
        cleanSearchParams<ProfessionalSearch>(professionalSearch),
        httpOptions
      )
      .pipe(
        map((professionals: Professional[]) =>
          this.addConcernsFlag<Professional>(professionals)
        ),
        tap((_) => this.log(`searchProfessionals`)),
        catchError(handleError<Professional[]>(`searchProfessionals`, this.notificationService, this.store))
      );
  }

  searchPaginatedProfessionals(
    professionalSearch: ProfessionalSearch
  ): Observable<ProfessionalSearch> {
    const url = `${this.url}/professionals/search2`;
    return this.http
      .post<ProfessionalSearch>(
        url,
        cleanSearchParams<ProfessionalSearch>(professionalSearch),
        httpOptions
      )
      .pipe(
        map((professionalsResults: ProfessionalSearch) => {
          professionalsResults.pageResult.content =
            this.addConcernsFlag<Professional>(
              professionalsResults.pageResult.content
            );
          return professionalsResults;
        }),
        tap((_) => this.log(`searchPaginatedProfessionals`)),
        catchError(handleError<ProfessionalSearch>(`searchPaginatedProfessionals`, this.notificationService, this.store))
      );
  }

  exportProviders(providerSearch: ProviderSearch) {
    return this.http
      .post(`${this.url}/providers/export`, { ...providerSearch, pageNumber: 0 }, {
        ...httpOptions,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          saveAs(response.body, response.headers.get('File-Name') || 'providers_export.csv');
          this.notificationService.showNotification('Export successful', 'Close', 'success', 3000);
        }),
        tap((_) => this.log(`exportProviders`)),
        catchError(handleError<ProviderSearch>(`exportProviders`, this.notificationService, this.store))
      );
  }

  exportProfessionals(professionalSearch: ProfessionalSearch) {
    return this.http
      .post(`${this.url}/professionals/export`, { ...professionalSearch, pageNumber: 0 }, {
        ...httpOptions,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          saveAs(response.body, response.headers.get('File-Name') || 'professionals_export.csv');
          this.notificationService.showNotification('Export successful', 'Close', 'success', 3000);
        }),
        tap((_) => this.log(`exportProfessionals`)),
        catchError(handleError<ProviderSearch>(`exportProfessionals`, this.notificationService, this.store))
      );
  }

  private addConcernsFlag<T extends { events?: any[] }>(results: T[]): T[] {
    if (!Array.isArray(results) || results.length == 0) {
      return [];
    }

    return results.map((result) => ({
      ...result,
      hasConcerns: result.events?.length > 0 || false,
      hasConcernsString: result.events?.length > 0 ? 'concerns' : '',
    }));
  }
}
